import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Translator } from '@/i18n/config';
import {
  Alert,
  Button,
  Card,
  Form as AntForm,
  Grid,
  Input,
  Select,
  Space,
  Spin,
  Switch,
  Tabs,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { History, Save, Trash2 } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { z } from 'zod';
import { Form } from '@shared/components/Form/Form';
import { FormField } from '@shared/components/Form/FormField';
import { PageHeader } from '@shared/components/PageHeader/PageHeader';
import { SlugInput } from '@shared/components/SlugInput/SlugInput';
import { RichTextEditor } from '@shared/components/RichTextEditor';
import { SeoPanel, type SeoPanelValue } from '@shared/components/SeoPanel';
import { KeywordsInput } from '@shared/components/KeywordsInput';
import { CustomCodeField } from '@shared/components/CustomCodeField/CustomCodeField';
import { ImageField } from '@shared/components/MediaUpload';
import { StatusBadge } from '@shared/components/StatusBadge';
import { useConfirm } from '@shared/components/ConfirmDialog';
import { articlesApi } from '@api/articles';
import { categoriesApi } from '@api/categories';
import { tagsApi } from '@api/tags';
import { authorsApi } from '@api/authors';
import { NormalizedApiError } from '@api/errors';
import { ErrorCode } from '@shared/constants/errorCodes';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { recordAuditAction } from '@features/audit/recordAction';
import { useAuth } from '@features/auth/hooks/useAuth';
import { useCan } from '@shared/rbac/useCan';
import { PERMISSIONS } from '@shared/rbac/permissions.const';
import { ROUTES } from '@shared/constants/routes';
import { LanguagePicker } from '@shared/components/LanguagePicker/LanguagePicker';
import { VariantPanel } from '@shared/components/VariantPanel/VariantPanel';
import { useActiveLanguages, pickDefaultLanguage } from '@features/languages/useLanguages';
import { ArticleVersionsDrawer } from '../components/ArticleVersionsDrawer';
import { ArticleActivityPanel } from '../components/ArticleActivityPanel';
import {
  toContentStatus,
  useArticleStatusLabel,
  type Article,
  type ArticleStatus,
  type ArticleWriteInput,
} from '../types';

const { Text } = Typography;

const SLUG_RULE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

function makeSchema(t: Translator) {
  return z.object({
    title: z
      .string()
      .min(5, t('form.validation.titleMin'))
      .max(200, t('form.validation.titleMax')),
    slug: z
      .string()
      .min(2, t('form.validation.slugMin'))
      .max(200)
      .regex(SLUG_RULE, t('form.validation.slugRegex')),
    body: z
      .string()
      .refine(
        (v) => v.replace(/<[^>]+>/g, '').trim().length >= 50,
        t('form.validation.bodyMin'),
      ),
    excerpt: z.string().max(500, t('form.validation.excerptMax')).default(''),
    categoryId: z
      .string()
      .min(1, t('form.validation.categoryRequired'))
      .uuid(t('form.validation.categoryUuid')),
    authorId: z.string().uuid(t('form.validation.authorUuid')).nullable(),
    tagIds: z.array(z.string().uuid()).max(12, t('form.validation.tagsMax')),
    coverImageUrl: z.string().max(500).optional().nullable(),
    metaTitle: z.string().max(200).optional().nullable(),
    metaDescription: z.string().max(500).optional().nullable(),
    canonicalTag: z.string().max(500).optional().nullable(),
    customCode: z.string().max(5000).optional().nullable(),
    keywords: z.string().max(500, t('form.validation.keywordsMax')).optional().nullable(),
    allowIndexing: z.boolean(),
    changeNote: z.string().max(500).optional().nullable(),
  });
}

type Values = z.infer<ReturnType<typeof makeSchema>>;

function emptyValues(): Values {
  return {
    title: '',
    slug: '',
    body: '',
    excerpt: '',
    categoryId: '',
    authorId: null,
    tagIds: [],
    coverImageUrl: null,
    metaTitle: null,
    metaDescription: null,
    canonicalTag: null,
    customCode: null,
    keywords: null,
    allowIndexing: true,
    changeNote: null,
  };
}

function toValues(a: Article): Values {
  return {
    title: a.title,
    slug: a.slug,
    body: a.body,
    excerpt: a.excerpt,
    categoryId: a.categoryId ?? '',
    authorId: a.authorId,
    tagIds: a.tagIds,
    coverImageUrl: a.coverImageUrl,
    metaTitle: a.metaTitle,
    metaDescription: a.metaDescription,
    canonicalTag: a.canonicalTag,
    customCode: a.customCode,
    keywords: a.keywords,
    allowIndexing: a.allowIndexing,
    changeNote: null,
  };
}

/** Reading time @ 200 wpm. */
function computeReadingTime(html: string): number {
  const words = html
    .replace(/<[^>]+>/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;
  return Math.max(1, Math.ceil(words / 200));
}

export function ArticleEditPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const [searchParams] = useSearchParams();
  // Varyant ekleme akışı: create formuna ?groupId=&lang= ile gelinir.
  const variantGroupId = searchParams.get('groupId') ?? undefined;
  const variantLang = searchParams.get('lang') ?? undefined;
  const { t } = useTranslation(['articles', 'common', 'validation', 'errors']);
  const statusLabel = useArticleStatusLabel();
  const { data: activeLanguages = [] } = useActiveLanguages();
  // Seçili içerik dili (create) — effect'siz türetme: kullanıcı seçimi > varyant
  // param'ı > varsayılan dil (set-state-in-effect'ten kaçınır).
  const [pickedLanguageId, setPickedLanguageId] = useState<string | undefined>(variantLang);
  const languageId = pickedLanguageId ?? pickDefaultLanguage(activeLanguages)?.id;
  const schema = useMemo(() => makeSchema(t as unknown as Translator), [t]);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const { user } = useAuth();
  // BE policy `content.publish` is admin-only in the seed. We gate publish-
  // grade workflow actions on `user.role === 'ADMIN'` per Foundation guidance.
  const isAdmin = user?.role === 'ADMIN';
  // K-6 — CustomCode alanı yalnız content.customcode yetkisiyle düzenlenebilir.
  const canEditCustomCode = useCan(PERMISSIONS.CONTENT_CUSTOM_CODE);

  const [versionsOpen, setVersionsOpen] = useState(false);

  const { data: article, isLoading } = useQuery({
    queryKey: ['articles', id],
    queryFn: () => articlesApi.get(id ?? ''),
    enabled: !isNew,
  });

  const { data: categoriesData } = useQuery({
    queryKey: ['categories', { scope: 'ARTICLE' }],
    queryFn: () => categoriesApi.list({ scope: 'ARTICLE' }),
    retry: false,
  });

  const { data: tagsData } = useQuery({
    queryKey: ['tags', { pageSize: 100 }],
    queryFn: () => tagsApi.list({ pageSize: 100, sort: 'name' }),
    retry: false,
  });

  const { data: authorsData } = useQuery({
    queryKey: ['authors', { pageSize: 100 }],
    queryFn: () => authorsApi.list({ pageSize: 100 }),
    retry: false,
  });

  const initialValues = useMemo<Values>(
    () => (article ? toValues(article) : emptyValues()),
    [article],
  );

  const handleMutationError = (err: unknown) => {
    if (err instanceof NormalizedApiError) {
      // K-5 — dört-göz 409'u slug çakışması DEĞİLDİR; kendi Türkçe mesajıyla
      // (errors.json) gösterilmeli, generic 409 dalına düşmemeli.
      if (
        err.code === ErrorCode.WORKFLOW_SELF_APPROVAL_FORBIDDEN ||
        err.code === ErrorCode.WORKFLOW_REVIEW_REQUIRED
      ) {
        toast.error(resolveApiErrorMessage(err));
        return;
      }
      if (err.status === 409 || err.code === ErrorCode.SLUG_CONFLICT) {
        toast.error(t('toast.slugConflict'));
        return;
      }
      if (err.code === ErrorCode.INVALID_SLUG) {
        toast.error(t('toast.invalidSlug'));
        return;
      }
    }
    toast.error(resolveApiErrorMessage(err));
  };

  const createMutation = useMutation({
    mutationFn: (input: ArticleWriteInput) => articlesApi.create(input),
    onSuccess: async (createdId, input) => {
      toast.success(t('toast.createdDraft'));
      await qc.invalidateQueries({ queryKey: ['articles'] });
      void recordAuditAction({
        action: 'article.create',
        resource: 'article',
        resourceId: createdId,
        summary: t('audit.created', { title: input.title }),
      });
      void navigate(ROUTES.ARTICLE_EDIT.replace(':id', createdId));
    },
    onError: handleMutationError,
  });

  const updateMutation = useMutation({
    mutationFn: (vars: { id: string; input: ArticleWriteInput }) =>
      articlesApi.update(vars.id, vars.input),
    onSuccess: async (_d, vars) => {
      toast.success(t('toast.saved'));
      await qc.invalidateQueries({ queryKey: ['articles'] });
      await qc.invalidateQueries({ queryKey: ['articles', vars.id] });
      await qc.invalidateQueries({ queryKey: ['article-versions', vars.id] });
      void recordAuditAction({
        action: 'article.update',
        resource: 'article',
        resourceId: vars.id,
        summary: t('audit.updated', { title: vars.input.title }),
      });
    },
    onError: handleMutationError,
  });

  const submitReviewMutation = useMutation({
    mutationFn: () => articlesApi.submitReview(id ?? ''),
    onSuccess: async () => {
      toast.success(t('toast.submittedReview'));
      await qc.invalidateQueries({ queryKey: ['articles', id] });
      await qc.invalidateQueries({ queryKey: ['articles'] });
      void recordAuditAction({
        action: 'article.review.submit',
        resource: 'article',
        resourceId: id,
        summary: t('audit.reviewSubmitted', { title: article?.title ?? t('audit.fallbackArticle') }),
      });
    },
    onError: handleMutationError,
  });

  const approveMutation = useMutation({
    mutationFn: () => articlesApi.approve(id ?? '', true),
    onSuccess: async () => {
      toast.success(t('toast.approved'));
      await qc.invalidateQueries({ queryKey: ['articles', id] });
      await qc.invalidateQueries({ queryKey: ['articles'] });
      void recordAuditAction({
        action: 'article.review.approve',
        resource: 'article',
        resourceId: id,
        summary: t('audit.approved', { title: article?.title ?? t('audit.fallbackArticle') }),
      });
    },
    onError: handleMutationError,
  });

  const rejectMutation = useMutation({
    mutationFn: (note: string) => articlesApi.approve(id ?? '', false, note),
    onSuccess: async (_d, note) => {
      toast.success(t('toast.rejected'));
      await qc.invalidateQueries({ queryKey: ['articles', id] });
      await qc.invalidateQueries({ queryKey: ['articles'] });
      void recordAuditAction({
        action: 'article.review.reject',
        resource: 'article',
        resourceId: id,
        summary: t('audit.rejected', { note }),
      });
    },
    onError: handleMutationError,
  });

  const publishMutation = useMutation({
    mutationFn: () => articlesApi.publish(id ?? ''),
    onSuccess: async () => {
      toast.success(t('toast.published'));
      await qc.invalidateQueries({ queryKey: ['articles', id] });
      await qc.invalidateQueries({ queryKey: ['articles'] });
      void recordAuditAction({
        action: 'article.publish',
        resource: 'article',
        resourceId: id,
        summary: t('audit.published', { title: article?.title ?? t('audit.fallbackArticle') }),
      });
    },
    onError: handleMutationError,
  });

  const archiveMutation = useMutation({
    mutationFn: () => articlesApi.archive(id ?? ''),
    onSuccess: async () => {
      toast.success(t('toast.archived'));
      await qc.invalidateQueries({ queryKey: ['articles', id] });
      await qc.invalidateQueries({ queryKey: ['articles'] });
      void recordAuditAction({
        action: 'article.archive',
        resource: 'article',
        resourceId: id,
        summary: t('audit.archived', { title: article?.title ?? t('audit.fallbackArticle') }),
      });
    },
    onError: handleMutationError,
  });

  const deleteMutation = useMutation({
    mutationFn: () => articlesApi.remove(id ?? ''),
    onSuccess: async () => {
      toast.success(t('toast.deletedSingle'));
      await qc.invalidateQueries({ queryKey: ['articles'] });
      void recordAuditAction({
        action: 'article.delete',
        resource: 'article',
        resourceId: id,
        summary: t('audit.deletedSingle', { title: article?.title ?? t('audit.fallbackArticle') }),
      });
      void navigate(ROUTES.ARTICLES);
    },
    onError: handleMutationError,
  });

  const featureMutation = useMutation({
    mutationFn: (next: boolean) =>
      articlesApi.bulk(next ? 'feature' : 'unfeature', [id ?? '']),
    onSuccess: async (_d, next) => {
      toast.success(next ? t('toast.featured') : t('toast.unfeatured'));
      await qc.invalidateQueries({ queryKey: ['articles', id] });
      await qc.invalidateQueries({ queryKey: ['articles'] });
      await qc.invalidateQueries({ queryKey: ['articles-featured'] });
      void recordAuditAction({
        action: 'article.featured.toggle',
        resource: 'article',
        resourceId: id,
        summary: t('audit.featuredToggle', {
          title: article?.title ?? t('audit.fallbackArticle'),
          state: next ? t('audit.featuredOn') : t('audit.featuredOff'),
        }),
      });
    },
    onError: handleMutationError,
  });

  const mutationsBusy =
    submitReviewMutation.isPending ||
    approveMutation.isPending ||
    rejectMutation.isPending ||
    publishMutation.isPending ||
    archiveMutation.isPending;

  const onSubmit = (values: Values) => {
    const payload: ArticleWriteInput = {
      title: values.title,
      slug: values.slug,
      body: values.body,
      excerpt: values.excerpt,
      categoryId: values.categoryId,
      authorId: values.authorId,
      tagIds: values.tagIds,
      coverImageUrl: values.coverImageUrl ?? null,
      metaTitle: values.metaTitle ?? null,
      metaDescription: values.metaDescription ?? null,
      canonicalTag: values.canonicalTag ?? null,
      customCode: values.customCode ?? null,
      keywords: values.keywords ?? null,
      allowIndexing: values.allowIndexing,
      changeNote: values.changeNote ?? null,
    };
    if (isNew) {
      createMutation.mutate({
        ...payload,
        languageId: languageId ?? null,
        groupId: variantGroupId ?? null,
      });
    } else {
      updateMutation.mutate({ id: id ?? '', input: payload });
    }
  };

  const currentStatus: ArticleStatus = article?.status ?? 'DRAFT';

  if (!isNew && isLoading) {
    return (
      <div style={{ padding: 64, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }

  if (!isNew && !article) {
    return (
      <div>
        <PageHeader
          title={t('page.notFoundTitle')}
          breadcrumb={[
            { label: t('common:labels.home'), to: '/' },
            { label: t('breadcrumb.list'), to: ROUTES.ARTICLES },
            { label: t('breadcrumb.notFound') },
          ]}
        />
        <Alert
          type="warning"
          showIcon
          message={t('page.notFoundMessage')}
          action={
            <Button size="small" onClick={() => void navigate(ROUTES.ARTICLES)}>
              {t('actions.backToList')}
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={isNew ? t('page.newTitle') : article?.title ?? t('page.editTitle')}
        description={
          isNew
            ? t('page.newDescription')
            : t('page.editDescription', { slug: article?.slug })
        }
        breadcrumb={[
          { label: t('common:labels.home'), to: '/' },
          { label: t('breadcrumb.list'), to: ROUTES.ARTICLES },
          { label: isNew ? t('breadcrumb.new') : t('breadcrumb.edit') },
        ]}
        actions={
          <Space wrap>
            <Button
              type="primary"
              icon={<Save size={14} />}
              htmlType="submit"
              form="article-edit-form"
              loading={createMutation.isPending || updateMutation.isPending}
            >
              {isNew ? t('actions.createDraft') : t('common:actions.save')}
            </Button>
          </Space>
        }
      />

      <Form
        id="article-edit-form"
        schema={schema}
        defaultValues={initialValues}
        onSubmit={onSubmit}
        options={{ values: initialValues }}
      >
        {({ watch, setValue, formState }) => {
          const titleValue = watch('title');
          const slugValue = watch('slug');
          const body = watch('body');
          const excerpt = watch('excerpt');
          const categoryId = watch('categoryId');
          const tagIds = watch('tagIds');
          const authorId = watch('authorId');
          const coverImageUrl = watch('coverImageUrl');
          const metaTitle = watch('metaTitle');
          const metaDescription = watch('metaDescription');
          const keywords = watch('keywords');
          const allowIndexing = watch('allowIndexing');

          const seoValue: SeoPanelValue = {
            title: metaTitle ?? '',
            description: metaDescription ?? '',
            slug: `makaleler/${slugValue}`,
            ogImage: coverImageUrl ? { url: coverImageUrl } : null,
          };

          const readingTime = computeReadingTime(body);

          return (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '1fr 320px',
                gap: 16,
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {!isNew &&
                (currentStatus === 'APPROVED' || currentStatus === 'IN_REVIEW') ? (
                  <Alert
                    type="warning"
                    showIcon
                    message={t('common:contentWorkflow.approvalInvalidationTitle')}
                    description={t('common:contentWorkflow.approvalInvalidationDescription')}
                  />
                ) : null}
                <Card title={t('form.cards.basic')}>
                  <AntForm layout="vertical" component="div">
                    <FormField name="title" label={t('form.labels.title')} required>
                      <Input
                        size="large"
                        maxLength={200}
                        showCount
                        placeholder={t('form.placeholders.title')}
                      />
                    </FormField>
                    <FormField name="slug" label={t('form.labels.slug')} required>
                      <SlugInput
                        value={slugValue}
                        onChange={(v) =>
                          setValue('slug', v, {
                            shouldDirty: true,
                            shouldValidate: true,
                          })
                        }
                        sourceValue={titleValue}
                        placeholder={t('form.placeholders.slug')}
                        prefix={t('form.slugPrefix')}
                      />
                    </FormField>
                    <FormField name="excerpt" label={t('form.labels.excerpt')}>
                      <Input.TextArea
                        value={excerpt}
                        onChange={(e) =>
                          setValue('excerpt', e.target.value, {
                            shouldDirty: true,
                            shouldValidate: true,
                          })
                        }
                        rows={3}
                        showCount
                        maxLength={500}
                        placeholder={t('form.placeholders.excerpt')}
                      />
                    </FormField>
                  </AntForm>
                </Card>

                <Card
                  title={t('form.cards.body')}
                  extra={
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {t('form.readingTime', { count: readingTime })}
                    </Text>
                  }
                >
                  <RichTextEditor
                    value={body}
                    onChange={(v) =>
                      setValue('body', v, {
                        shouldDirty: true,
                        shouldValidate: true,
                      })
                    }
                    placeholder={t('form.placeholders.body')}
                    maxLength={20000}
                  />
                  {formState.errors.body ? (
                    <div
                      style={{
                        marginTop: 6,
                        color: 'var(--ds-danger)',
                        fontSize: 12,
                      }}
                    >
                      {formState.errors.body.message as string}
                    </div>
                  ) : null}
                </Card>

                <Card title={t('form.cards.cover')}>
                  <ImageField
                    value={coverImageUrl}
                    onChange={(url) => setValue('coverImageUrl', url, { shouldDirty: true })}
                    label={t('form.coverLabel')}
                    maxSizeMB={5}
                    hint={t('form.coverHint')}
                  />
                </Card>

                <Card>
                  <Tabs
                    defaultActiveKey="seo"
                    items={[
                      {
                        key: 'seo',
                        label: t('tabs.seo'),
                        children: (
                          <>
                            <SeoPanel
                              value={seoValue}
                              onChange={(next) => {
                                setValue('metaTitle', next.title || null, {
                                  shouldDirty: true,
                                });
                                setValue('metaDescription', next.description || null, {
                                  shouldDirty: true,
                                });
                                // The article entity exposes a single
                                // coverImageUrl field which doubles as the
                                // og:image source — keep them in sync so an
                                // upload from the SEO panel doesn't drop on
                                // the floor.
                                setValue(
                                  'coverImageUrl',
                                  next.ogImage?.url ?? null,
                                  { shouldDirty: true },
                                );
                              }}
                              maxTitleLength={200}
                              maxDescriptionLength={500}
                            />
                            <AntForm layout="vertical" component="div" style={{ marginTop: 12 }}>
                              <FormField name="canonicalTag" label={t('form.labels.canonical')}>
                                <Input placeholder={t('form.placeholders.canonical')} />
                              </FormField>
                              <AntForm.Item
                                label={t('form.labels.keywords')}
                                help={t('form.keywordsHint')}
                              >
                                <KeywordsInput
                                  value={keywords}
                                  onChange={(v) =>
                                    setValue('keywords', v, { shouldDirty: true })
                                  }
                                  placeholder={t('form.placeholders.keywords')}
                                />
                              </AntForm.Item>
                              <FormField name="customCode" label={t('form.labels.customCode')}>
                                <CustomCodeField canEdit={canEditCustomCode} />
                              </FormField>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                                <Switch
                                  checked={allowIndexing}
                                  onChange={(v) =>
                                    setValue('allowIndexing', v, { shouldDirty: true })
                                  }
                                />
                                <span style={{ fontSize: 13 }}>
                                  {t('form.allowIndexing')}
                                </span>
                              </div>
                            </AntForm>
                          </>
                        ),
                      },
                      {
                        key: 'versions',
                        label: t('tabs.versions'),
                        children: isNew ? (
                          <Alert
                            type="info"
                            showIcon
                            message={t('tabs.versionsEmpty')}
                          />
                        ) : (
                          <Space
                            direction="vertical"
                            size={10}
                            style={{ width: '100%' }}
                          >
                            <Button
                              icon={<History size={14} />}
                              onClick={() => setVersionsOpen(true)}
                            >
                              {t('tabs.openAllVersions')}
                            </Button>
                          </Space>
                        ),
                      },
                      {
                        key: 'activity',
                        label: t('tabs.activity'),
                        children: isNew ? (
                          <Alert
                            type="info"
                            showIcon
                            message={t('tabs.activityEmpty')}
                          />
                        ) : (
                          <ArticleActivityPanel articleId={id ?? ''} open />
                        ),
                      },
                    ]}
                  />
                </Card>
              </div>

              <aside style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {isNew ? (
                  <Card size="small" title={t('common:contentLanguage.label')}>
                    <LanguagePicker
                      value={languageId}
                      onChange={setPickedLanguageId}
                      disabled={Boolean(variantLang)}
                      style={{ width: '100%' }}
                    />
                    {variantGroupId ? (
                      <div style={{ marginTop: 8, fontSize: 12, color: 'var(--ds-text-secondary)' }}>
                        {t('variant.addingHint')}
                      </div>
                    ) : null}
                  </Card>
                ) : article ? (
                  <VariantPanel
                    currentLanguageId={article.languageId}
                    variantLanguageCodes={article.variantLanguageCodes}
                    contentType="article"
                    groupId={article.groupId}
                    onOpenVariant={(variantId) =>
                      void navigate(ROUTES.ARTICLE_EDIT.replace(':id', variantId))
                    }
                    onAddVariant={(lang) =>
                      void navigate(
                        `${ROUTES.ARTICLE_NEW}?groupId=${article.groupId}&lang=${lang.id}`,
                      )
                    }
                  />
                ) : null}
                <Card size="small" title={t('form.cards.statusFlow')}>
                  {!isNew && article ? (
                    <div style={{ marginBottom: 12 }}>
                      <StatusBadge
                        kind="content"
                        status={toContentStatus(currentStatus)}
                        size="sm"
                      />
                      <span style={{ fontSize: 12, marginLeft: 8, color: 'var(--ds-text-secondary)' }}>
                        {statusLabel(currentStatus)}
                      </span>
                    </div>
                  ) : null}
                  {isNew ? (
                    <Alert
                      type="info"
                      showIcon
                      message={t('statusPanel.draftAlertMessage')}
                      description={t('statusPanel.draftAlertDescription', { status: statusLabel('DRAFT') })}
                    />
                  ) : (
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                      {currentStatus === 'DRAFT' || currentStatus === 'REJECTED' ? (
                        <Button
                          block
                          type="primary"
                          loading={submitReviewMutation.isPending}
                          onClick={() => submitReviewMutation.mutate()}
                          disabled={mutationsBusy}
                        >
                          {t('statusPanel.submitReview')}
                        </Button>
                      ) : null}

                      {currentStatus === 'IN_REVIEW' && isAdmin ? (
                        <>
                          <Button
                            block
                            type="primary"
                            loading={approveMutation.isPending}
                            onClick={() => approveMutation.mutate()}
                            disabled={mutationsBusy}
                          >
                            {t('statusPanel.approve')}
                          </Button>
                          <Button
                            block
                            danger
                            loading={rejectMutation.isPending}
                            onClick={() => {
                              void (async () => {
                                const ok = await confirm({
                                  title: t('statusPanel.rejectConfirmTitle'),
                                  description: t('statusPanel.rejectConfirmDescription'),
                                  okText: t('statusPanel.rejectConfirmOk'),
                                });
                                if (!ok) return;
                                const note =
                                  window.prompt(
                                    t('statusPanel.rejectPrompt'),
                                    t('statusPanel.rejectPromptDefault'),
                                  ) ?? '';
                                if (note.trim().length === 0) {
                                  toast.error(t('toast.rejectReasonRequired'));
                                  return;
                                }
                                rejectMutation.mutate(note.trim());
                              })();
                            }}
                            disabled={mutationsBusy}
                          >
                            {t('statusPanel.reject')}
                          </Button>
                        </>
                      ) : null}

                      {(currentStatus === 'APPROVED' ||
                        currentStatus === 'DRAFT' ||
                        currentStatus === 'ARCHIVED') &&
                      isAdmin ? (
                        <Button
                          block
                          type="primary"
                          loading={publishMutation.isPending}
                          onClick={() => publishMutation.mutate()}
                          disabled={mutationsBusy}
                        >
                          {t('statusPanel.publish')}
                        </Button>
                      ) : null}

                      {currentStatus === 'PUBLISHED' && isAdmin ? (
                        <Button
                          block
                          loading={archiveMutation.isPending}
                          onClick={() => {
                            void (async () => {
                              const ok = await confirm({
                                title: t('statusPanel.archiveConfirmTitle'),
                                description: t('statusPanel.archiveConfirmDescription', { title: article?.title }),
                              });
                              if (ok) archiveMutation.mutate();
                            })();
                          }}
                          disabled={mutationsBusy}
                        >
                          {t('statusPanel.archive')}
                        </Button>
                      ) : null}

                      {!isAdmin && currentStatus !== 'DRAFT' && currentStatus !== 'REJECTED' ? (
                        <Alert
                          type="warning"
                          showIcon
                          message={t('statusPanel.adminOnly')}
                        />
                      ) : null}
                    </Space>
                  )}
                </Card>

                <Card size="small" title={t('form.cards.categoryRequired')}>
                  <Select<string>
                    style={{ width: '100%' }}
                    placeholder={t('form.placeholders.category')}
                    value={categoryId || undefined}
                    onChange={(v) =>
                      setValue('categoryId', v, {
                        shouldDirty: true,
                        shouldValidate: true,
                      })
                    }
                    showSearch
                    optionFilterProp="label"
                    options={(categoriesData?.items ?? []).map((c) => ({
                      value: c.id,
                      label: c.name,
                    }))}
                  />
                  {formState.errors.categoryId ? (
                    <div
                      style={{
                        marginTop: 4,
                        color: 'var(--ds-danger)',
                        fontSize: 12,
                      }}
                    >
                      {formState.errors.categoryId.message as string}
                    </div>
                  ) : null}
                </Card>

                <Card size="small" title={t('form.cards.tags')}>
                  <Select<string[]>
                    mode="multiple"
                    style={{ width: '100%' }}
                    placeholder={t('form.placeholders.tags')}
                    value={tagIds}
                    onChange={(v) =>
                      setValue('tagIds', v.slice(0, 12), {
                        shouldDirty: true,
                        shouldValidate: true,
                      })
                    }
                    options={(tagsData?.items ?? []).slice(0, 100).map((t) => ({
                      value: t.id,
                      label: t.name,
                    }))}
                    maxTagCount="responsive"
                    optionFilterProp="label"
                  />
                </Card>

                <Card size="small" title={t('form.cards.author')}>
                  <Select<string | null>
                    style={{ width: '100%' }}
                    allowClear
                    placeholder={t('form.placeholders.author')}
                    value={authorId}
                    onChange={(v) =>
                      setValue('authorId', v ?? null, { shouldDirty: true })
                    }
                    options={(authorsData?.items ?? []).map((a) => ({
                      value: a.id,
                      label: a.displayName,
                    }))}
                    optionFilterProp="label"
                  />
                </Card>

                {!isNew && article ? (
                  <Card size="small" title={t('form.cards.featured')}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 13 }}>{t('featuredPanel.question')}</span>
                      <Switch
                        checked={article.isFeatured}
                        disabled={!isAdmin || featureMutation.isPending}
                        loading={featureMutation.isPending}
                        onChange={(checked) => featureMutation.mutate(checked)}
                      />
                    </div>
                    {article.isFeatured ? (
                      <div style={{ marginTop: 6, fontSize: 12, color: 'var(--ds-text-tertiary)' }}>
                        {t('featuredPanel.order', { order: (article.featuredOrder ?? 0) + 1 })}
                      </div>
                    ) : null}
                  </Card>
                ) : null}

                {!isNew && article ? (
                  <Card size="small" title={t('form.cards.info')}>
                    <Space direction="vertical" size={6} style={{ width: '100%', fontSize: 12 }}>
                      <div>
                        <Text type="secondary">{t('info.readingTime')}</Text>
                        {article.readingTime} {t('info.readingTimeUnit')}
                      </div>
                      <div>
                        <Text type="secondary">{t('info.viewCount')}</Text>
                        {article.viewCount}
                      </div>
                      {article.publishedAt ? (
                        <div>
                          <Text type="secondary">{t('info.publishedAt')}</Text>
                          {dayjs(article.publishedAt).format('DD.MM.YYYY HH:mm')}
                        </div>
                      ) : null}
                      <div>
                        <Text type="secondary">{t('info.updatedAt')}</Text>
                        {dayjs(article.updatedAt).format('DD.MM.YYYY HH:mm')}
                      </div>
                    </Space>
                  </Card>
                ) : null}

                {!isNew && article ? (
                  <Card size="small" title={t('form.cards.actions')}>
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                      <Button
                        block
                        icon={<History size={14} />}
                        onClick={() => setVersionsOpen(true)}
                      >
                        {t('buttons.versions')}
                      </Button>
                      {isAdmin ? (
                        <Button
                          block
                          danger
                          icon={<Trash2 size={14} />}
                          loading={deleteMutation.isPending}
                          onClick={() => {
                            void (async () => {
                              const ok = await confirm({
                                title: t('deleteConfirm.title'),
                                description: t('deleteConfirm.description', { title: article.title }),
                                okText: t('deleteConfirm.ok'),
                              });
                              if (ok) deleteMutation.mutate();
                            })();
                          }}
                        >
                          {t('common:actions.delete')}
                        </Button>
                      ) : null}
                      <Button block onClick={() => void navigate(ROUTES.ARTICLES)}>
                        {t('buttons.backToList')}
                      </Button>
                    </Space>
                  </Card>
                ) : null}
              </aside>
            </div>
          );
        }}
      </Form>

      {!isNew && id ? (
        <ArticleVersionsDrawer
          open={versionsOpen}
          articleId={id}
          currentBody={article?.body ?? ''}
          onClose={() => setVersionsOpen(false)}
        />
      ) : null}
    </div>
  );
}
